"use client"
import {isEmpty, map, slice} from "lodash";
import uniqBy from "lodash/uniqBy";
import useSWR from "swr";

import styles from "./SearchResults.module.scss";
import Link from "next/link";
import Card from "@/app/components/product/card/ProductCard";
import CardRow from "@/app/components/product/card/row/Card";
import Button from "@/components/_other/button/Button";
import SearchResultsSkeleton from "@/components/header/search/SearchResultsSkeleton";
import {useTranslation} from "@/app/i18n/client";
import {useRouter} from "next/navigation";
import TagManager from "@/grandus-lib/utils/gtag";
import EnhancedEcommerce from "@/utils/ecommerce";


const Categories = ({ categories, clearSearch }) => {
    const { t } = useTranslation();
    const router = useRouter();
    if (isEmpty(categories)) {
        return null;
    }

    const handleClick = category => (e) => {
        e.preventDefault();
        clearSearch();
        router.push(`/kategoria/${category?.urlName}`);
    }

    return (
        <div>
            <h4>{t('search.categories')}</h4>
            <ul className={styles.categories}>
                {map(slice(categories, 0, 6), category => (
                    <li key={`search-category-${category.id}`}>
                        <Link
                            href={`/kategoria/${category?.urlName}`}
                            aria-label={`link to ${category?.name}`}
                            title={category.name ?? ''}
                            onClick={handleClick(category)}
                        >
                            {category.name}
                        </Link>
                    </li>
                ))}
            </ul>
        </div>
    )
}

const MainProduct = ({ product, clearSearch }) => {
    const router = useRouter();
    const { t } = useTranslation();
    if (isEmpty(product)) {
        return;
    }

    const handleClick = (e) => {
        e.preventDefault();
        clearSearch();
        // TagManager.push(
        //     EnhancedEcommerce.productClick(
        //         { ...product },
        //         "search",
        //     ),
        //     () => {
        //         router.push(`/produkt/${product?.urlTitle}`)
        //     }
        // );
        router.push(`/produkt/${product?.urlTitle}`);
    }

    return (
        <>
            <h4>{t('search.top_product')}</h4>
            <Card product={product} className={styles.productCard} onClick={handleClick} />
        </>
    )
}

const Products = ({ products, term, clearSearch }) => {
    const router = useRouter();
    const { t } = useTranslation();
    if (isEmpty(products)) {
        return;
    }

    const productsCount = products.length;
    const productsToRender = productsCount > 8 ? slice(products, 0, 8) : products;

    const handleShowAllClick = (e) => {
        e.preventDefault();
        clearSearch();
        router.push(`/vyhladavanie/${term}`);
    }

    const handleProductClick = product => (e) => {
        e.preventDefault();
        clearSearch();
        // TagManager.push(
        //     EnhancedEcommerce.productClick(
        //         { ...product },
        //         "search",
        //     ),
        //     () => {
        //         router.push(`/produkt/${product?.urlTitle}`)
        //     }
        // );
        router.push(`/produkt/${product?.urlTitle}`);
    }

    return (
        <>
            <h4>{t('search.products')}</h4>
            <div className={styles.wrapper}>
                {map(productsToRender, product => <CardRow key={`search-product-${product.id}`} isSimple={true} product={product} className={`${styles.productCard}`} onClick={handleProductClick(product)} />)}
            </div>
            {productsCount > 8 ? <div className={styles.showMoreButton}><Button htmlType="a" href={`/vyhladavanie/${term}`} onClick={handleShowAllClick}>Zobraziť všetky produkty</Button></div> : null}
        </>
    )
}

const Brands = ({ brands, matchedBrands, clearSearch }) => {
    const router = useRouter();
    const finalBrands = uniqBy(
        !isEmpty(matchedBrands) ? matchedBrands : brands,
        "urlName"
    );

    if (isEmpty(finalBrands)) {
        return;
    }

    const handleClick = brand => (e) => {
        e.preventDefault();
        clearSearch();
        router.push(`/kategoria/${brand?.urlName}`);
    }

    return (
        <>
            {map(finalBrands, brand => (
                <Link href={`/kategoria/${brand?.urlName}`} key={`search-brand-${brand?.urlName}`} onClick={handleClick(brand)}>
                    {brand.name}
                </Link>
            ))}
        </>
    )
}

const Blogs = ({ blogs, clearSearch }) => {
    const router = useRouter();
    const { t } = useTranslation();

    const handleClick = blog => (e) => {
        e.preventDefault();
        clearSearch();
        router.push(`/blog/${blog?.urlTitle}`);
    }

    return (
        <div>
            <h4>{t('search.blogs')}</h4>
            <ul className={styles.blogs}>
                {map(blogs, blog => (
                    <li key={`search-blog-${blog.id}`}>
                        <Link
                            href={`/blog/${blog?.urlTitle}`}
                            aria-label={`link to ${blog?.title}`}
                            title={blog.title ?? ''}
                            onClick={handleClick(blog)}
                        >
                            {blog.title}
                        </Link>
                    </li>
                ))}
            </ul>
        </div>
    )
}

const SearchResults = ({ term, clearSearch }) => {
    const { t } = useTranslation();
    const {data: results, isLoading: loading} = useSWR(
        term ? '/api/search/autocomplete?search=' + encodeURIComponent(term) : null,
        (url) => fetch(url).then((r) => r.json()),
        {
            // revalidateOnFocus: false,
            revalidateOnReconnect: false,
            shouldRetryOnError: false,
            dedupingInterval: 30000,
        }
    );

    if (isEmpty(term)) {
        return null;
    }

    if (loading) {
        return <SearchResultsSkeleton />
    }

    return (
        <div className={styles.results}>
            <div className={styles.left}>
                <Categories
                    categories={!isEmpty(results?.matchedCategories) ? results.matchedCategories : results?.categories}
                    clearSearch={clearSearch}
                />
                <Blogs blogs={results?.blogs} clearSearch={clearSearch} />
            </div>
            <div className={styles.right}>
                {!isEmpty(results?.products) ? (
                    <>
                        <div className={styles.mainProduct}>
                            <MainProduct product={results?.products[0]} clearSearch={clearSearch} />
                        </div>
                        <div className={styles.products}>
                            <Products products={slice(results?.products, 1)} term={term} clearSearch={clearSearch} />
                        </div>
                    </>
                ) : <div className={styles.empty}>{t('search.empty_products')}</div>}
                {/*{results?.brands.length > 1 ?*/}
                {/*    <div className={styles.brands}>*/}
                {/*        <Brands brands={results?.brands} clearSearch={clearSearch} />*/}
                {/*    </div>*/}
                {/*    : null}*/}
            </div>
        </div>
    );
};

export default SearchResults;
